import React from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../Typography";

const HorizontalFormField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required = false,
  disabled = false,
  placeholder,
  multiline = false,
  rows = 3,
  options = [],
  startIcon,
  infoIcon,
  className = "",
  render,
  errors = {},
  touched = {},
  formData = {},
  handleInputChange,
  handleBlur,
  ...props
}) => {
  const baseInputClass = `w-full rounded-lg border ${
    error ? "border-red-400" : "border-slate-200"
  } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:bg-slate-100 disabled:cursor-not-allowed`;

  const renderField = () => {
    if (render) {
      return render(value, formData || props.formData || {}, handleInputChange || onChange || (() => {}), error, errors, touched);
    }

    if (type === "select") {
      return (
        <div className="relative">
          {startIcon && (
            <Icon
              icon={startIcon}
              className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
            />
          )}
          <select
            name={name}
            value={value || ""}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            required={required}
            className={`${baseInputClass} ${
              startIcon ? "pl-10" : "pl-4"
            } pr-10 appearance-none`}
          >
            {placeholder && (
              <option value="">{placeholder}</option>
            )}
            {options.map((option) => (
              <option
                key={typeof option === "object" ? option.value : option}
                value={typeof option === "object" ? option.value : option}
              >
                {typeof option === "object" ? option.label : option}
              </option>
            ))}
          </select>
          <Icon
            icon="lucide:chevron-down"
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
          />
        </div>
      );
    }

    if (multiline || type === "textarea") {
      return (
        <textarea
          name={name}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          required={required}
          className={`${baseInputClass} resize-none`}
          {...props}
        />
      );
    }

    return (
      <div className="relative">
        {startIcon && (
          <Icon
            icon={startIcon}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
          />
        )}
        <input
          type={type}
          name={name}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`${baseInputClass} ${
            startIcon ? "pl-10" : "pl-4"
          }`}
          {...props}
        />
      </div>
    );
  };

  const hasLabelColumn = Boolean((label && String(label).trim()) || infoIcon);

  return (
    <div
      className={`${hasLabelColumn ? "flex items-start gap-6" : "flex flex-col"} py-4 ${className}`}
    >
      {/* Label Section — omit column when no label (keeps checkbox / custom rows flush left) */}
      {hasLabelColumn && (
        <div className="flex-shrink-0 w-48">
          <div className="flex items-center gap-2">
            {label && (
              <label
                htmlFor={name}
                className="text-sm font-medium text-slate-700 text-left"
              >
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {infoIcon && (
              <Icon
                icon={infoIcon}
                className="h-4 w-4 text-slate-400"
                title={helperText}
              />
            )}
          </div>
        </div>
      )}

      {/* Field Section */}
      <div className={hasLabelColumn ? "flex-1 min-w-0" : "w-full"}>
        {renderField()}
        {error && typeof error === 'string' && (
          <p className="text-xs text-red-500 mt-1">{error}</p>
        )}
        {helperText && !error && (
          <p className="text-xs text-slate-500 mt-1">{helperText}</p>
        )}
        {props.maxLength && (type === "textarea" || multiline) && (
          <Typography
            as="p"
            className={`text-xs mt-1 ${
              value && value.length > props.maxLength
                ? "text-red-500"
                : "text-tertiary-600"
            }`}
          >
            {props.maxLength - (value?.length || 0)} characters left
          </Typography>
        )}
      </div>
    </div>
  );
};

export default HorizontalFormField;

